// The analytics page's state in the address: what the page shows is read
// from the query string and written back to it, so that a copied address or
// a reload shows the same view over the same selection. The module is pure:
// it knows nothing of the router or of React.
//
//   /analytics?view=capability-map
//             &q=gyro&capability=backbone:capability_part:nav.determine-position
//             &cm.grain=mode
//
// - `view` names the view: treemap (the default, left out), capability-map,
//   ecosystem, supply-flow, matrix, missions or partnerships.
// - The selection is written as Explore writes it (see exploreState): q,
//   category, capability, mission, condition, mode, level, status, maker,
//   maker_country, operator, operator_country, programme, numeric, unnamed.
//   Explore's sort, page and view mode are not
//   part of it: `view` here is the analytics view, not Explore's cards/table.
// - Each view's own settings sit under a prefix of the view's own
//   (`cm.` capability map, `eco.` ecosystem, `sf.` supply flow, `tm.`
//   treemap, `mx.` matrix, `ms.` missions, `pt.` partnerships), so that two
//   views never share a parameter.
//   Switching view keeps every view's settings.
//
// A view registers its settings in three places: its settings type in
// `AnalyticsSettings`, its codec in `VIEW_SETTINGS` (defaults, decode,
// encode), and, when it reads with them, a request builder beside
// `toCapabilityMapParams`.

import {
  CapabilityMapParams,
  EcosystemParams,
  MatrixParams,
  MissionsParams,
  PartnershipsParams,
  SupplyFlowParams,
  TreemapParams,
} from "../../services/productPlaneApi";
import {
  CapabilityGrain,
  EcosystemCentreType,
  MatrixColumns,
  MatrixRank,
  MatrixRows,
  MatrixSort,
  MissionLevel,
  MissionProvider,
  PartnershipGrain,
  TreemapMeasure,
} from "../../types/analytics";
import {
  DEFAULT_EXPLORE_STATE,
  EXPLORE_FILTER_NAMES,
  ExploreState,
  countExploreFilters,
  decodeExploreState,
  encodeExploreRange,
  encodeExploreState,
} from "../Explore/exploreState";
import { toCountryCode, toTrustedSet } from "../ValueChain/valueChainState";
import {
  CAPABILITY_SETS,
  CUSTOM,
  GENERAL_AREA_ID,
  MOST_CLAIMED,
  presetOf,
} from "./Matrix/matrixPresets";

export type AnalyticsView =
  | "capability-map"
  | "ecosystem"
  | "supply-flow"
  | "treemap"
  | "matrix"
  | "missions"
  | "partnerships";

export const ANALYTICS_VIEWS: readonly AnalyticsView[] = [
  "treemap",
  "capability-map",
  "ecosystem",
  "supply-flow",
  "matrix",
  "missions",
  "partnerships",
];

// The views the view switch offers, in its order. A view left out is
// hidden: its pane, its read and its settings stay, but the switch does not
// offer it and an address that names it opens the default view. The supply
// flow is hidden until it is redesigned: its hosts column holds only hosts
// inside the scope, and a whole-graph scope draws thousands of bands.
export const SHOWN_ANALYTICS_VIEWS: readonly AnalyticsView[] = ANALYTICS_VIEWS.filter(
  (view) => view !== "supply-flow",
);

// How each view reads in the view switch
export const ANALYTICS_VIEW_LABELS: Record<AnalyticsView, string> = {
  "capability-map": "Capability map",
  ecosystem: "Ecosystem",
  "supply-flow": "Supply flow",
  treemap: "Treemap",
  matrix: "Matrix",
  missions: "Missions",
  partnerships: "Partnerships",
};

export const CAPABILITY_GRAINS: readonly CapabilityGrain[] = [
  "core",
  "condition",
  "mode",
];

// The selection: the parts of Explore's state that choose the products.
// Sort, page and the cards/table mode are Explore's own and are left out.
export type AnalyticsSelection = Pick<
  ExploreState,
  | "keyword"
  | "unnamed"
  | "numeric"
  | "category"
  | "capability"
  | "mission"
  | "condition"
  | "mode"
  | "level"
  | "status"
  | "maker"
  | "maker_country"
  | "operator"
  | "operator_country"
  | "programme"
>;

export interface CapabilityMapSettings {
  grain: CapabilityGrain;
}

export const ECOSYSTEM_CENTRE_TYPES: readonly EcosystemCentreType[] = [
  "product",
  "organization",
  "project",
  "kind",
];

export const ECOSYSTEM_DEPTHS: readonly EcosystemDepth[] = [1, 2];

export type EcosystemDepth = 1 | 2;

export interface EcosystemSettings {
  // What the centre is; meaningful only with a centre id
  centreType: EcosystemCentreType;
  // The centre's id; empty while none is chosen
  centreId: string;
  // Rings drawn around the centre
  depth: EcosystemDepth;
}

export interface SupplyFlowSettings {
  // The home jurisdiction; null while the address names none (the view then
  // falls back to the one remembered for the user)
  home: string | null;
  // The countries trusted beside it
  trusted: string[];
}

export const TREEMAP_MEASURES: readonly TreemapMeasure[] = [
  "products",
  "makers",
  "articles",
];

// What colours a class of the treemap
export type TreemapColour = "products" | "makers" | "domestic";

export const TREEMAP_COLOURS: readonly TreemapColour[] = [
  "products",
  "makers",
  "domestic",
];

export interface TreemapSettings {
  // What a class is sized by
  measure: TreemapMeasure;
  // What a class is coloured by
  colour: TreemapColour;
  // The class the treemap is drilled into; null at the top
  root: string | null;
  // Whether the classes with nothing on record are drawn. They hold no
  // product, so the area they take says nothing: off unless asked for
  gaps: boolean;
  // The home jurisdiction for the domestic share; null while the address
  // names none (the view then falls back to the one remembered for the user)
  home: string | null;
  // The countries trusted beside it
  trusted: string[];
}

export const MATRIX_ROWS: readonly MatrixRows[] = [
  "products",
  "categories",
  "makers",
  "capabilities",
];

export const MATRIX_COLUMNS: readonly MatrixColumns[] = [
  "capabilities",
  "conditions",
  "modes",
];

export const MATRIX_RANKS: readonly MatrixRank[] = ["group", "type", "subtype"];

export const MATRIX_SORTS: readonly MatrixSort[] = ["count", "name"];

// Product rows of a page
export const MATRIX_PAGE_SIZE = 50;

// The columns a kind of row is read against: the five pairs the matrix reads
// are products, categories or makers against capabilities, and capabilities
// against conditions or modes. The first is the one a change of rows moves to.
export const columnsAdmittedBy = (rows: MatrixRows): readonly MatrixColumns[] =>
  rows === "capabilities" ? ["conditions", "modes"] : ["capabilities"];

export interface MatrixSettings {
  // What a row is
  rows: MatrixRows;
  // What a column is; always one the rows admit
  columns: MatrixColumns;
  // What a capability column stands for
  grain: CapabilityGrain;
  // The rank of the classes that are the rows of a category matrix
  rank: MatrixRank;
  // Narrows the rows to those whose name holds the text; empty for none
  find: string;
  // A column id: narrows the rows to those said to have the column
  claiming: string | null;
  sort: MatrixSort;
  // A row id: that row comes first on every page
  pin: string | null;
  // The page of product rows, counted from 1
  page: number;
  // The abilities of the capability axis: the most claimed, a preset or a
  // custom set (see matrixPresets)
  capabilitySet: string;
  // The abilities of a custom set, in their order; empty for any other set
  capabilities: string[];
}

// Most abilities a custom set holds: what the backend reads
export const MAX_CHOSEN_CAPABILITIES = 40;

// The abilities a capability set chooses; null for the most claimed
export const chosenCapabilities = (settings: MatrixSettings): readonly string[] | null =>
  settings.capabilitySet === CUSTOM
    ? settings.capabilities
    : (presetOf(settings.capabilitySet)?.abilities ?? null);

// The settings after a change. What the change invalidates is reset in the
// same step: a row id means nothing under other rows, a column id nothing
// under other columns, and a page nothing under another narrowing. A value
// already in force invalidates nothing.
export const changeMatrixSettings = (
  settings: MatrixSettings,
  change: Partial<MatrixSettings>,
): MatrixSettings => {
  const next = { ...settings, ...change };
  const differs = (name: keyof MatrixSettings) =>
    name in change && change[name] !== settings[name];
  if (differs("rows")) {
    if (!columnsAdmittedBy(next.rows).includes(next.columns)) {
      next.columns = columnsAdmittedBy(next.rows)[0];
    }
    next.find = "";
    next.claiming = null;
    next.pin = null;
    next.page = 1;
  }
  if (
    differs("columns") ||
    differs("grain") ||
    differs("capabilitySet") ||
    differs("capabilities")
  ) {
    next.claiming = null;
    next.page = 1;
  }
  if (differs("rank")) {
    next.pin = null;
    next.page = 1;
  }
  if (differs("find") || differs("claiming") || differs("sort")) {
    next.page = 1;
  }
  return next;
};

// Every level a capability can be held at, in the order the backend lists
// them, `operator` the ring of missions; the rings hold all of them by
// default
export const MISSION_LEVELS: readonly MissionLevel[] = [
  "operator",
  "platform",
  "infrastructure",
  "module",
  "component",
  "unknown",
];

// Both kinds of provider edge, in the order the backend lists them
export const MISSION_PROVIDERS: readonly MissionProvider[] = ["inside", "could"];

export interface MissionsSettings {
  // The capability pick at the centre (a mission, an effect or function
  // area, a core, or a capability); null for the mission the most hosts of
  // the scope claim
  centre: string | null;
  // The condition and the mode the centre's claim must state; null for any
  condition: string | null;
  mode: string | null;
  // The levels the rings hold; never empty
  levels: MissionLevel[];
  // The kinds of provider edge drawn; never empty
  providers: MissionProvider[];
}

// The settings with a new centre picked: its qualifiers are let go, since
// a condition or a mode is offered for one centre and may not be stated
// with another
export const withCentre = (settings: MissionsSettings, centre: string | null): MissionsSettings => ({
  ...settings,
  centre,
  condition: null,
  mode: null,
});

// How the partnerships are drawn: the groups in their clusters, the same
// groups as a matrix of pairs, or one organization with its partners
export type PartnershipsMode = "clusters" | "matrix" | "partners";

export const PARTNERSHIPS_MODES: readonly PartnershipsMode[] = [
  "clusters",
  "matrix",
  "partners",
];

// Coarser to finer, as the grain menu lists them
export const PARTNERSHIP_GRAINS: readonly PartnershipGrain[] = ["coarse", "medium", "fine"];

export interface PartnershipsSettings {
  mode: PartnershipsMode;
  // How finely the groups are split into clusters
  grain: PartnershipGrain;
  // The organization whose partners are drawn; read in the partners mode
  // only, and kept while another mode is shown
  focusId: string | null;
  // How many clusters are drawn; null for the backend's default. A change
  // of it keeps the cluster expanded: ids do not depend on the count.
  clusters: number | null;
  // How many groups of each cluster are drawn, the most strongly tied
  // first; null for the backend's default
  perCluster: number | null;
  // A cluster id: that cluster is drawn with all its groups. An id means
  // nothing under another scope, grain or mode, so a change of any of them
  // lets it go.
  expand: number | null;
}

// The clusters the backend draws when none is asked for, and the most it
// draws; "All" in the menu asks for the most
export const PARTNERSHIPS_CLUSTERS_DEFAULT = 9;
export const PARTNERSHIPS_CLUSTERS_MOST = 100;

// The numbers of clusters the menu offers, the last one standing for all
export const PARTNERSHIP_CLUSTER_COUNTS: readonly number[] = [
  6,
  9,
  12,
  15,
  20,
  30,
  50,
  PARTNERSHIPS_CLUSTERS_MOST,
];

// The groups of each cluster the backend draws when none is asked for, and
// the most it draws in all; "All" in the menu asks for the most
export const PARTNERSHIPS_PER_CLUSTER_DEFAULT = 8;
export const PARTNERSHIPS_GROUPS_MOST = 600;

// The numbers of groups per cluster the menu offers, the last for all
export const PARTNERSHIP_PER_CLUSTER_COUNTS: readonly number[] = [
  5,
  PARTNERSHIPS_PER_CLUSTER_DEFAULT,
  12,
  20,
  PARTNERSHIPS_GROUPS_MOST,
];

// The settings after a change. What the change invalidates is reset in the
// same step: a cluster id means nothing under another grain or mode. A
// value already in force invalidates nothing.
export const changePartnershipsSettings = (
  settings: PartnershipsSettings,
  change: Partial<PartnershipsSettings>,
): PartnershipsSettings => {
  const next = { ...settings, ...change };
  const differs = (name: keyof PartnershipsSettings) =>
    name in change && change[name] !== settings[name];
  if ((differs("grain") || differs("mode")) && !differs("expand")) {
    next.expand = null;
  }
  return next;
};

// The values of a list setting that are known, each once, in their own
// order; an empty result is the whole list, since the backend refuses an
// empty one
const knownList = <T extends string>(
  allowed: readonly T[],
  values: readonly string[],
): T[] => {
  const kept = allowed.filter((value) => values.includes(value));
  return kept.length > 0 ? kept : [...allowed];
};

// Whether a list setting holds every value it can
const isWhole = <T extends string>(allowed: readonly T[], values: readonly T[]): boolean =>
  allowed.every((value) => values.includes(value));

// The settings of every view. The views to come fill their slot with
// their own type (and codec, below).
export interface AnalyticsSettings {
  "capability-map": CapabilityMapSettings;
  ecosystem: EcosystemSettings;
  "supply-flow": SupplyFlowSettings;
  treemap: TreemapSettings;
  matrix: MatrixSettings;
  missions: MissionsSettings;
  partnerships: PartnershipsSettings;
}

export interface AnalyticsState extends AnalyticsSelection {
  view: AnalyticsView;
  settings: AnalyticsSettings;
}

// How one view's settings go to and from the address. A setting is read and
// written by its bare key; the prefix is added by the module.
export interface ViewSettingsCodec<S> {
  // The prefix of the view's parameters, with its dot
  prefix: string;
  defaults: S;
  // Reads the settings; a missing or unknown value is its default
  decode: (read: (key: string) => string | null) => S;
  // Writes the settings that differ from the defaults
  encode: (settings: S, write: (key: string, value: string) => void) => void;
}

const oneOf = <T extends string>(
  allowed: readonly T[],
  value: string | null,
  fallback: T,
): T => (allowed.includes(value as T) ? (value as T) : fallback);

export const VIEW_SETTINGS: {
  [View in AnalyticsView]: ViewSettingsCodec<AnalyticsSettings[View]>;
} = {
  "capability-map": {
    prefix: "cm.",
    defaults: { grain: "core" },
    decode: (read) => ({ grain: oneOf(CAPABILITY_GRAINS, read("grain"), "core") }),
    encode: (settings, write) => {
      if (settings.grain !== "core") {
        write("grain", settings.grain);
      }
    },
  },
  ecosystem: {
    prefix: "eco.",
    defaults: { centreType: "product", centreId: "", depth: 2 },
    decode: (read) => {
      const depth = read("depth");
      return {
        centreType: oneOf(ECOSYSTEM_CENTRE_TYPES, read("type"), "product"),
        centreId: read("id") ?? "",
        depth: depth === "1" ? 1 : 2,
      };
    },
    encode: (settings, write) => {
      // A type without an id says nothing
      if (settings.centreId) {
        write("type", settings.centreType);
        write("id", settings.centreId);
      }
      if (settings.depth !== 2) {
        write("depth", String(settings.depth));
      }
    },
  },
  "supply-flow": {
    prefix: "sf.",
    defaults: { home: null, trusted: [] },
    decode: (read) => ({
      home: toCountryCode(read("home")),
      trusted: toTrustedSet((read("trusted") ?? "").split(",")),
    }),
    encode: (settings, write) => {
      if (settings.home) {
        write("home", settings.home);
      }
      if (settings.trusted.length > 0) {
        write("trusted", settings.trusted.join(","));
      }
    },
  },
  treemap: {
    prefix: "tm.",
    defaults: {
      measure: "products",
      colour: "products",
      root: null,
      gaps: false,
      home: null,
      trusted: [],
    },
    decode: (read) => ({
      measure: oneOf(TREEMAP_MEASURES, read("measure"), "products"),
      colour: oneOf(TREEMAP_COLOURS, read("colour"), "products"),
      root: read("root") || null,
      gaps: read("gaps") === "1",
      home: toCountryCode(read("home")),
      trusted: toTrustedSet((read("trusted") ?? "").split(",")),
    }),
    encode: (settings, write) => {
      if (settings.measure !== "products") {
        write("measure", settings.measure);
      }
      if (settings.colour !== "products") {
        write("colour", settings.colour);
      }
      if (settings.root) {
        write("root", settings.root);
      }
      if (settings.gaps) {
        write("gaps", "1");
      }
      if (settings.home) {
        write("home", settings.home);
      }
      if (settings.trusted.length > 0) {
        write("trusted", settings.trusted.join(","));
      }
    },
  },
  matrix: {
    prefix: "mx.",
    defaults: {
      rows: "products",
      columns: "capabilities",
      grain: "core",
      rank: "type",
      find: "",
      claiming: null,
      sort: "count",
      pin: null,
      page: 1,
      capabilitySet: MOST_CLAIMED,
      capabilities: [],
    },
    decode: (read) => {
      const rows = oneOf(MATRIX_ROWS, read("rows"), "products");
      const admitted = columnsAdmittedBy(rows);
      // A pair none of the five is read as the columns valid for the rows
      const columns = oneOf(MATRIX_COLUMNS, read("cols"), admitted[0]);
      const page = Number(read("page"));
      const set = oneOf(CAPABILITY_SETS, read("set"), MOST_CLAIMED);
      const capabilities =
        set === CUSTOM
          ? Array.from(new Set((read("caps") ?? "").split(",").filter(Boolean))).slice(
              0,
              MAX_CHOSEN_CAPABILITIES,
            )
          : [];
      return {
        // A custom set of no ability is the most claimed
        capabilitySet: set === CUSTOM && capabilities.length === 0 ? MOST_CLAIMED : set,
        capabilities,
        rows,
        columns: admitted.includes(columns) ? columns : admitted[0],
        grain: oneOf(CAPABILITY_GRAINS, read("grain"), "core"),
        rank: oneOf(MATRIX_RANKS, read("rank"), "type"),
        find: read("find") ?? "",
        claiming: read("claiming") || null,
        sort: oneOf(MATRIX_SORTS, read("sort"), "count"),
        pin: read("pin") || null,
        page: Number.isInteger(page) && page > 1 ? page : 1,
      };
    },
    encode: (settings, write) => {
      if (settings.rows !== "products") {
        write("rows", settings.rows);
      }
      if (settings.columns !== "capabilities") {
        write("cols", settings.columns);
      }
      if (settings.grain !== "core") {
        write("grain", settings.grain);
      }
      if (settings.rank !== "type") {
        write("rank", settings.rank);
      }
      if (settings.find) {
        write("find", settings.find);
      }
      if (settings.claiming) {
        write("claiming", settings.claiming);
      }
      if (settings.sort !== "count") {
        write("sort", settings.sort);
      }
      if (settings.pin) {
        write("pin", settings.pin);
      }
      if (settings.page > 1) {
        write("page", String(settings.page));
      }
      if (settings.capabilitySet !== MOST_CLAIMED) {
        write("set", settings.capabilitySet);
      }
      if (settings.capabilitySet === CUSTOM && settings.capabilities.length > 0) {
        write("caps", settings.capabilities.join(","));
      }
    },
  },
  missions: {
    prefix: "ms.",
    defaults: {
      centre: null,
      condition: null,
      mode: null,
      levels: [...MISSION_LEVELS],
      providers: [...MISSION_PROVIDERS],
    },
    decode: (read) => ({
      // `ms.mission`, the centre's first name, is read for the links
      // already written; it is never written
      centre: read("centre") || read("mission") || null,
      condition: read("condition") || null,
      mode: read("mode") || null,
      levels: knownList(MISSION_LEVELS, (read("levels") ?? "").split(",")),
      providers: knownList(MISSION_PROVIDERS, (read("providers") ?? "").split(",")),
    }),
    encode: (settings, write) => {
      if (settings.centre) {
        write("centre", settings.centre);
      }
      if (settings.condition) {
        write("condition", settings.condition);
      }
      if (settings.mode) {
        write("mode", settings.mode);
      }
      const levels = knownList(MISSION_LEVELS, settings.levels);
      if (!isWhole(MISSION_LEVELS, levels)) {
        write("levels", levels.join(","));
      }
      const providers = knownList(MISSION_PROVIDERS, settings.providers);
      if (!isWhole(MISSION_PROVIDERS, providers)) {
        write("providers", providers.join(","));
      }
    },
  },
  partnerships: {
    prefix: "pt.",
    defaults: {
      mode: "clusters",
      grain: "medium",
      focusId: null,
      clusters: null,
      perCluster: null,
      expand: null,
    },
    decode: (read) => ({
      mode: oneOf(PARTNERSHIPS_MODES, read("mode"), "clusters"),
      grain: oneOf(PARTNERSHIP_GRAINS, read("grain"), "medium"),
      focusId: read("focus") || null,
      clusters: /^\d+$/.test(read("clusters") ?? "") &&
        Number(read("clusters")) >= 1 &&
        Number(read("clusters")) <= PARTNERSHIPS_CLUSTERS_MOST
        ? Number(read("clusters"))
        : null,
      perCluster: PARTNERSHIP_PER_CLUSTER_COUNTS.includes(Number(read("per")))
        ? Number(read("per"))
        : null,
      expand: /^\d+$/.test(read("expand") ?? "") ? Number(read("expand")) : null,
    }),
    encode: (settings, write) => {
      if (settings.mode !== "clusters") {
        write("mode", settings.mode);
      }
      if (settings.grain !== "medium") {
        write("grain", settings.grain);
      }
      if (settings.focusId) {
        write("focus", settings.focusId);
      }
      if (settings.clusters !== null) {
        write("clusters", String(settings.clusters));
      }
      if (settings.perCluster !== null) {
        write("per", String(settings.perCluster));
      }
      if (settings.expand !== null) {
        write("expand", String(settings.expand));
      }
    },
  },
};

const VIEW_PARAM = "view";

// The state of an address without parameters: every product, on the
// capability map
export const DEFAULT_ANALYTICS_STATE: AnalyticsState = {
  view: "treemap",
  keyword: DEFAULT_EXPLORE_STATE.keyword,
  unnamed: DEFAULT_EXPLORE_STATE.unnamed,
  numeric: [],
  category: [],
  capability: [],
  mission: [],
  condition: [],
  mode: [],
  level: [],
  status: [],
  maker: [],
  maker_country: [],
  operator: [],
  operator_country: [],
  programme: [],
  settings: {
    "capability-map": VIEW_SETTINGS["capability-map"].defaults,
    ecosystem: VIEW_SETTINGS.ecosystem.defaults,
    "supply-flow": VIEW_SETTINGS["supply-flow"].defaults,
    treemap: VIEW_SETTINGS.treemap.defaults,
    matrix: VIEW_SETTINGS.matrix.defaults,
    missions: VIEW_SETTINGS.missions.defaults,
    partnerships: VIEW_SETTINGS.partnerships.defaults,
  },
};

// The selection of a state: of Explore's, or of the page's own
export const selectionOf = (state: AnalyticsSelection): AnalyticsSelection => ({
  keyword: state.keyword,
  unnamed: state.unnamed,
  numeric: state.numeric,
  category: state.category,
  capability: state.capability,
  mission: state.mission,
  condition: state.condition,
  mode: state.mode,
  level: state.level,
  status: state.status,
  maker: state.maker,
  maker_country: state.maker_country,
  operator: state.operator,
  operator_country: state.operator_country,
  programme: state.programme,
});

// A selection as Explore's state, with Explore's own parts at their
// defaults: what the chips and the address of Explore are built from
export const toExploreState = (selection: AnalyticsSelection): ExploreState => ({
  ...DEFAULT_EXPLORE_STATE,
  ...selectionOf(selection),
});

// The number of active filters of the selection, as Explore counts them
export const countAnalyticsFilters = (selection: AnalyticsSelection): number =>
  countExploreFilters(toExploreState(selection));

// Reads the state from an address. A missing or unknown value is its
// default; an unknown parameter is ignored.
export const decodeAnalyticsState = (
  params: URLSearchParams,
): AnalyticsState => {
  const settings = {} as AnalyticsSettings;
  for (const view of ANALYTICS_VIEWS) {
    const { prefix, decode } = VIEW_SETTINGS[view];
    (settings as Record<AnalyticsView, unknown>)[view] = decode((key) =>
      params.get(`${prefix}${key}`),
    );
  }
  return {
    ...selectionOf(decodeExploreState(params)),
    view: oneOf(
      SHOWN_ANALYTICS_VIEWS,
      params.get(VIEW_PARAM),
      DEFAULT_ANALYTICS_STATE.view,
    ),
    settings,
  };
};

// Writes the state into an address. The parameters of `base` that are not
// the page's own (Explore's, `view`, the views' prefixes) are kept; `base`
// itself is not changed.
export const encodeAnalyticsState = (
  state: AnalyticsState,
  base?: URLSearchParams,
): URLSearchParams => {
  const prefixes = ANALYTICS_VIEWS.map((view) => VIEW_SETTINGS[view].prefix);
  const kept = new URLSearchParams(base);
  for (const name of Array.from(kept.keys())) {
    if (prefixes.some((prefix) => name.startsWith(prefix))) {
      kept.delete(name);
    }
  }
  // Explore's encoder clears its own names, `view` among them
  const params = encodeExploreState(toExploreState(state), kept);

  if (state.view !== DEFAULT_ANALYTICS_STATE.view) {
    params.set(VIEW_PARAM, state.view);
  }
  for (const view of ANALYTICS_VIEWS) {
    const { prefix, encode } = VIEW_SETTINGS[view];
    (encode as (settings: unknown, write: (k: string, v: string) => void) => void)(
      state.settings[view],
      (key, value) => params.set(`${prefix}${key}`, value),
    );
  }
  return params;
};

// The state after a change; the settings of the views not named are kept.
// A change of the selection lets go of the cluster expanded: its id means
// nothing under another scope.
export const changeAnalyticsState = (
  state: AnalyticsState,
  change: Partial<AnalyticsState>,
): AnalyticsState => {
  const next = { ...state, ...change };
  if (
    next.settings.partnerships.expand !== null &&
    JSON.stringify(selectionOf(next)) !== JSON.stringify(selectionOf(state))
  ) {
    next.settings = {
      ...next.settings,
      partnerships: { ...next.settings.partnerships, expand: null },
    };
  }
  return next;
};

export const ANALYTICS_PATH = "/analytics";
export const EXPLORE_PATH = "/explore";

const withQuery = (path: string, params: URLSearchParams): string => {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
};

// The address of the analytics page over a selection (Explore's state or
// the page's own), on the default view
export const analyticsAddress = (selection: AnalyticsSelection): string =>
  withQuery(
    ANALYTICS_PATH,
    encodeAnalyticsState({ ...DEFAULT_ANALYTICS_STATE, ...selectionOf(selection) }),
  );

// The query string of a selection alone, as Explore writes it: what the
// session remembers as the last scope and the sidebar entry reopens
export const scopeQueryOf = (selection: AnalyticsSelection): string =>
  encodeExploreState(toExploreState(selection)).toString();

// The address of Explore over the selection of a state
export const exploreAddress = (selection: AnalyticsSelection): string =>
  withQuery(EXPLORE_PATH, encodeExploreState(toExploreState(selection)));

// The request of the capability map for a state: the selection as the
// product search takes it, then the map's settings
export const toCapabilityMapParams = (
  state: Pick<AnalyticsState, "settings"> & AnalyticsSelection,
): CapabilityMapParams => {
  const params: CapabilityMapParams = {};
  if (state.keyword) {
    params.keyword = state.keyword;
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      params[name] = state[name];
    }
  }
  if (state.numeric.length > 0) {
    params.numeric = state.numeric.map(encodeExploreRange);
  }
  if (state.unnamed) {
    params.include_unnamed = true;
  }
  if (state.settings["capability-map"].grain !== "core") {
    params.grain = state.settings["capability-map"].grain;
  }
  return params;
};

// The request of the ecosystem for a state: the selection as the product
// search takes it, then the centre (when one is chosen) and the depth
export const toEcosystemParams = (
  state: Pick<AnalyticsState, "settings"> & AnalyticsSelection,
): EcosystemParams => {
  const { centreType, centreId, depth } = state.settings.ecosystem;
  const params: EcosystemParams = {};
  if (state.keyword) {
    params.keyword = state.keyword;
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      params[name] = state[name];
    }
  }
  if (state.numeric.length > 0) {
    params.numeric = state.numeric.map(encodeExploreRange);
  }
  if (state.unnamed) {
    params.include_unnamed = true;
  }
  if (centreId) {
    params.centre_type = centreType;
    params.centre_id = centreId;
  }
  params.depth = depth;
  return params;
};

// The request of the supply flow for a state: the selection as the product
// search takes it, then the jurisdictions. `fallbackHome` is the home the
// address does not name (the one remembered for the user). The trusted set
// is read only with a home, so it is sent only with one.
export const toSupplyFlowParams = (
  state: Pick<AnalyticsState, "settings"> & AnalyticsSelection,
  fallbackHome: string | null = null,
): SupplyFlowParams => {
  const { home, trusted } = state.settings["supply-flow"];
  const params: SupplyFlowParams = {};
  if (state.keyword) {
    params.keyword = state.keyword;
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      params[name] = state[name];
    }
  }
  if (state.numeric.length > 0) {
    params.numeric = state.numeric.map(encodeExploreRange);
  }
  if (state.unnamed) {
    params.include_unnamed = true;
  }
  const effectiveHome = home ?? fallbackHome;
  if (effectiveHome) {
    params.home_jurisdiction = effectiveHome;
    if (trusted.length > 0) {
      params.trusted_jurisdictions = trusted;
    }
  }
  return params;
};

// The request of the treemap for a state: the selection as the product
// search takes it, then the measure and the jurisdictions. `fallbackHome` is
// the home the address does not name (the one remembered for the user). The
// trusted set is read only with a home, so it is sent only with one. The
// drill root and the colour are the view's own and are not asked of the
// backend.
export const toTreemapParams = (
  state: Pick<AnalyticsState, "settings"> & AnalyticsSelection,
  fallbackHome: string | null = null,
): TreemapParams => {
  const { measure, home, trusted } = state.settings.treemap;
  const params: TreemapParams = {};
  if (state.keyword) {
    params.keyword = state.keyword;
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      params[name] = state[name];
    }
  }
  if (state.numeric.length > 0) {
    params.numeric = state.numeric.map(encodeExploreRange);
  }
  if (state.unnamed) {
    params.include_unnamed = true;
  }
  params.measure = measure;
  const effectiveHome = home ?? fallbackHome;
  if (effectiveHome) {
    params.home_jurisdiction = effectiveHome;
    if (trusted.length > 0) {
      params.trusted_jurisdictions = trusted;
    }
  }
  return params;
};

// The request of the matrix for a state: the selection as the product search
// takes it, then the table. Rows and columns are always sent: the backend
// refuses capability rows without their columns. The grain is read with
// capability columns only and the rank with category rows only; the page is
// asked of the backend as a limit and an offset.
export const toMatrixParams = (
  state: Pick<AnalyticsState, "settings"> & AnalyticsSelection,
): MatrixParams => {
  const { rows, columns, grain, rank, find, claiming, sort, pin, page } =
    state.settings.matrix;
  const params: MatrixParams = {};
  if (state.keyword) {
    params.keyword = state.keyword;
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      params[name] = state[name];
    }
  }
  if (state.numeric.length > 0) {
    params.numeric = state.numeric.map(encodeExploreRange);
  }
  if (state.unnamed) {
    params.include_unnamed = true;
  }
  params.rows = rows;
  params.columns = columns;
  if (columns === "capabilities") {
    params.grain = grain;
  }
  if (rows === "categories") {
    params.rank = rank;
  }
  if (find.trim()) {
    params.find = find.trim();
  }
  if (claiming) {
    params.claiming = claiming;
  }
  params.sort = sort;
  if (pin) {
    params.pin = pin;
  }
  params.limit = MATRIX_PAGE_SIZE;
  params.offset = (page - 1) * MATRIX_PAGE_SIZE;
  // The capability axis: the chosen abilities, or the most claimed with the
  // general ones left out
  const chosen = chosenCapabilities(state.settings.matrix);
  if (chosen && chosen.length > 0) {
    params.capabilities = [...chosen];
  } else {
    params.skip_areas = [GENERAL_AREA_ID];
  }
  return params;
};

// The request of the missions view for a state: the selection as the
// product search takes it, then the centre and its qualifiers (when chosen)
// and the levels and providers. The qualifiers go as `centre_condition` and
// `centre_mode`: `condition` and `mode` are the scope's own filters, and
// would narrow it. The two lists are always sent and never empty: the
// backend refuses an empty one.
export const toMissionsParams = (
  state: Pick<AnalyticsState, "settings"> & AnalyticsSelection,
): MissionsParams => {
  const { centre, condition, mode, levels, providers } = state.settings.missions;
  const params: MissionsParams = {};
  if (state.keyword) {
    params.keyword = state.keyword;
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      params[name] = state[name];
    }
  }
  if (state.numeric.length > 0) {
    params.numeric = state.numeric.map(encodeExploreRange);
  }
  if (state.unnamed) {
    params.include_unnamed = true;
  }
  if (centre) {
    params.centre = centre;
  }
  if (condition) {
    params.centre_condition = condition;
  }
  if (mode) {
    params.centre_mode = mode;
  }
  params.levels = knownList(MISSION_LEVELS, levels);
  params.providers = knownList(MISSION_PROVIDERS, providers);
  return params;
};

// The request of the partnerships for a state: the selection as the
// product search takes it, then the grain, and the organization in focus
// in the partners mode only. The clusters and the matrix read the same
// response, and with it the number of clusters, of groups per cluster and
// the cluster expanded; none is read round a focus.
export const toPartnershipsParams = (
  state: Pick<AnalyticsState, "settings"> & AnalyticsSelection,
): PartnershipsParams => {
  const { mode, grain, focusId, clusters, perCluster, expand } = state.settings.partnerships;
  const params: PartnershipsParams = {};
  if (state.keyword) {
    params.keyword = state.keyword;
  }
  for (const name of EXPLORE_FILTER_NAMES) {
    if (state[name].length > 0) {
      params[name] = state[name];
    }
  }
  if (state.numeric.length > 0) {
    params.numeric = state.numeric.map(encodeExploreRange);
  }
  if (state.unnamed) {
    params.include_unnamed = true;
  }
  params.grain = grain;
  if (mode === "partners" && focusId) {
    params.focus_id = focusId;
  }
  if (mode !== "partners") {
    if (clusters !== null) {
      params.clusters = clusters;
    }
    if (perCluster !== null) {
      params.per_cluster = perCluster;
    }
    if (expand !== null) {
      params.expand = expand;
    }
  }
  return params;
};
